import React, { useState, useRef, useCallback } from 'react';
import {
  exportImportService,
  parseImportFile,
  detectExportType,
  convertToImportFormat,
} from '../../services';
import { getWidgetIcon } from '../widgets/registry';
import Button from '../ui/Button';
import type {
  BundleImport,
  ImportPreview,
  ImportResult,
  ConflictResolution,
  WidgetTemplateImport,
  DashboardImport,
  WorkflowImport,
  WidgetInstanceImport,
} from '../../services/exportImportService';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: (result: ImportResult) => void;
  // Optional: restrict to specific types
  allowedTypes?: ('widget' | 'dashboard' | 'workflow' | 'bundle')[];
}

type ImportStep = 'upload' | 'preview' | 'options' | 'importing' | 'complete';

const ImportModal: React.FC<ImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete,
  allowedTypes = ['widget', 'dashboard', 'workflow', 'bundle'],
}) => {
  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<unknown>(null);
  const [exportType, setExportType] = useState<string>('unknown');
  const [importBundle, setImportBundle] = useState<BundleImport | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [conflictResolution, setConflictResolution] = useState<ConflictResolution>('rename');
  const [importAsDraft, setImportAsDraft] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Track renamed items (originalId or name -> newName)
  const [renamedItems, setRenamedItems] = useState<Record<string, string>>({});

  const resetState = useCallback(() => {
    setStep('upload');
    setFile(null);
    setParsedData(null);
    setExportType('unknown');
    setImportBundle(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setLoading(false);
    setConflictResolution('rename');
    setImportAsDraft(true);
    setRenamedItems({});
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setError(null);
    setLoading(true);

    try {
      const data = await parseImportFile(selectedFile);
      const type = detectExportType(data);

      if (type === 'unknown') {
        setError('Unrecognized file format. Please select a valid export file.');
        setLoading(false);
        return;
      }

      if (!allowedTypes.includes(type as 'widget' | 'dashboard' | 'workflow' | 'bundle')) {
        setError(`This import type (${type}) is not allowed here.`);
        setLoading(false);
        return;
      }

      setFile(selectedFile);
      setParsedData(data);
      setExportType(type);

      // Convert to import format
      const bundle = convertToImportFormat(data, type);
      bundle.options.conflictResolution = conflictResolution;
      bundle.options.importAsDraft = importAsDraft;
      setImportBundle(bundle);

      // Get preview
      const previewResult = await exportImportService.previewImport(bundle);
      setPreview(previewResult);
      setStep('preview');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/json') {
      // Create a synthetic event to reuse handleFileSelect
      const input = fileInputRef.current;
      if (input) {
        const dt = new DataTransfer();
        dt.items.add(droppedFile);
        input.files = dt.files;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };

  const handleUpdateOptions = async () => {
    if (!importBundle) return;

    setLoading(true);
    try {
      const updatedBundle = {
        ...importBundle,
        options: {
          conflictResolution,
          importAsDraft,
        },
      };
      setImportBundle(updatedBundle);

      // Refresh preview with new options
      const previewResult = await exportImportService.previewImport(updatedBundle);
      setPreview(previewResult);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update preview');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importBundle) return;

    setStep('importing');
    setLoading(true);
    setError(null);

    try {
      // Apply renamed items to the bundle before importing
      let finalBundle = importBundle;
      if (Object.keys(renamedItems).length > 0) {
        finalBundle = {
          ...importBundle,
          dashboards: importBundle.dashboards.map(d => ({
            ...d,
            name: renamedItems[d.originalId || d.name] || d.name,
          })),
          workflow: importBundle.workflow ? {
            ...importBundle.workflow,
            name: renamedItems[importBundle.workflow.originalId || importBundle.workflow.name] || importBundle.workflow.name,
          } : undefined,
        };
      }

      const importResult = await exportImportService.importBundle(finalBundle);
      setResult(importResult);
      setStep('complete');

      if (importResult.success) {
        onImportComplete(importResult);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Import failed');
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'widget': return 'Widget Template';
      case 'dashboard': return 'Dashboard';
      case 'workflow': return 'Workflow';
      case 'bundle': return 'Bundle (Workflow + Dependencies)';
      default: return 'Unknown';
    }
  };

  const getWidgetTypeIcon = (type: string): React.ReactNode => {
    return getWidgetIcon(type);
  };

  // Get detailed info from the import bundle
  const getDetailedWidgets = (): WidgetTemplateImport[] => {
    return importBundle?.widgetTemplates || [];
  };

  const getDetailedDashboards = (): DashboardImport[] => {
    return importBundle?.dashboards || [];
  };

  const getDetailedWorkflow = (): WorkflowImport | undefined => {
    return importBundle?.workflow;
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-surface rounded-xl shadow-2xl border border-neutral-200 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-neutral-900">
            Import {step === 'complete' ? 'Complete' : ''}
          </h2>
          <button
            onClick={handleClose}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Upload Step */}
          {step === 'upload' && (
            <div className="space-y-4">
              <p className="text-neutral-600">
                Select a JSON export file to import. Supported formats: Widget, Dashboard, Workflow, or Bundle.
              </p>

              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="border-2 border-dashed border-neutral-300 rounded-lg p-8 text-center hover:border-primary-400 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {loading ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-neutral-500">Parsing file...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <svg className="w-12 h-12 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <div>
                      <span className="text-primary-600 font-medium">Click to upload</span>
                      <span className="text-neutral-500"> or drag and drop</span>
                    </div>
                    <span className="text-sm text-neutral-400">JSON files only</span>
                  </div>
                )}
              </div>

              {error && (
                <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 text-danger-700">
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Preview Step */}
          {step === 'preview' && preview && (
            <div className="space-y-6">
              {/* File Info */}
              <div className="bg-neutral-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">{file?.name}</p>
                    <p className="text-sm text-neutral-500">{getTypeLabel(exportType)}</p>
                  </div>
                </div>
              </div>

              {/* Validation Errors */}
              {preview.validationErrors.length > 0 && (
                <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
                  <h4 className="font-medium text-danger-800 mb-2">Validation Errors</h4>
                  <ul className="list-disc list-inside text-danger-700 text-sm space-y-1">
                    {preview.validationErrors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Items to Import - Detailed View */}
              <div>
                <h4 className="font-medium text-neutral-900 mb-3">Items to Import ({preview.totalItems})</h4>

                {/* Workflow Section */}
                {getDetailedWorkflow() && (
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-neutral-600 mb-2">
                      Workflow
                    </h5>
                    <div className={`p-4 rounded-lg border ${preview.workflows[0]?.hasConflict ? 'border-warning-300 bg-warning-50' : 'border-primary-200 bg-primary-50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-neutral-900">{getDetailedWorkflow()?.name}</span>
                        {preview.workflows[0]?.hasConflict && (
                          <span className="text-xs bg-warning-200 text-warning-800 px-2 py-1 rounded">
                            Conflict
                          </span>
                        )}
                      </div>
                      {getDetailedWorkflow()?.description && (
                        <p className="text-sm text-neutral-600 mb-2">{getDetailedWorkflow()?.description}</p>
                      )}
                      <div className="text-xs text-neutral-500">
                        {(getDetailedWorkflow()?.flowData as any)?.nodes?.length || 0} steps
                        {getDetailedWorkflow()?.dashboards && getDetailedWorkflow()!.dashboards!.length > 0 && (
                          <span> • {getDetailedWorkflow()?.dashboards?.length} embedded dashboards</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Dashboards Section */}
                {getDetailedDashboards().length > 0 && (
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-neutral-600 mb-2">
                      Dashboards ({getDetailedDashboards().length})
                    </h5>
                    <div className="space-y-3">
                      {getDetailedDashboards().map((dashboard, i) => {
                        const previewItem = preview.dashboards.find(p => p.name === dashboard.name);
                        const widgetCount = dashboard.widgets?.length || 0;
                        const embeddedTemplates = dashboard.widgets?.filter(w => w.template).length || 0;
                        const itemKey = dashboard.originalId || dashboard.name;
                        // Use hasOwnProperty to distinguish between "not set" and "set to empty string"
                        const hasRename = Object.prototype.hasOwnProperty.call(renamedItems, itemKey);
                        const currentName = hasRename ? renamedItems[itemKey] : dashboard.name;
                        const isRenamed = hasRename && renamedItems[itemKey] !== dashboard.name && renamedItems[itemKey].trim() !== '';

                        return (
                          <div key={i} className={`p-4 rounded-lg border ${previewItem?.hasConflict && !isRenamed ? 'border-warning-300 bg-warning-50' : 'border-neutral-200 bg-surface'}`}>
                            <div className="flex items-center justify-between mb-2">
                              {previewItem?.hasConflict ? (
                                <div className="flex-1 mr-2">
                                  <input
                                    type="text"
                                    value={currentName}
                                    onChange={(e) => setRenamedItems(prev => ({
                                      ...prev,
                                      [itemKey]: e.target.value,
                                    }))}
                                    className={`w-full px-2 py-1 text-sm font-semibold rounded border ${
                                      isRenamed ? 'border-success-300 bg-success-50' : 'border-warning-300'
                                    }`}
                                    placeholder="Enter new name..."
                                  />
                                  {isRenamed && (
                                    <p className="text-xs text-success-600 mt-1">Renamed from "{dashboard.name}"</p>
                                  )}
                                </div>
                              ) : (
                                <span className="font-semibold text-neutral-900">{dashboard.name}</span>
                              )}
                              {previewItem?.hasConflict && !isRenamed && (
                                <span className="text-xs bg-warning-200 text-warning-800 px-2 py-1 rounded whitespace-nowrap">
                                  Conflict
                                </span>
                              )}
                              {isRenamed && (
                                <span className="text-xs bg-success-200 text-success-800 px-2 py-1 rounded whitespace-nowrap">
                                  Renamed
                                </span>
                              )}
                            </div>
                            {dashboard.description && (
                              <p className="text-sm text-neutral-600 mb-2">{dashboard.description}</p>
                            )}
                            <div className="text-xs text-neutral-500 mb-2">
                              {widgetCount} widget{widgetCount !== 1 ? 's' : ''}
                              {embeddedTemplates > 0 && (
                                <span className="text-success-600 ml-2">
                                  ({embeddedTemplates} template{embeddedTemplates !== 1 ? 's' : ''} embedded)
                                </span>
                              )}
                            </div>
                            {/* Show widget instances */}
                            {dashboard.widgets && dashboard.widgets.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-neutral-100">
                                <p className="text-xs text-neutral-500 mb-1">Widgets in this dashboard:</p>
                                <div className="flex flex-wrap gap-1">
                                  {dashboard.widgets.map((widget, wi) => (
                                    <span
                                      key={wi}
                                      className="inline-flex items-center gap-1 text-xs bg-neutral-100 text-neutral-700 px-2 py-1 rounded"
                                      title={widget.template ? `Type: ${widget.template.type}` : 'No template embedded'}
                                    >
                                      {widget.template && <span>{getWidgetTypeIcon(widget.template.type)}</span>}
                                      {widget.title}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Widget Templates Section */}
                {getDetailedWidgets().length > 0 && (
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-neutral-600 mb-2">
                      Widget Templates ({getDetailedWidgets().length})
                    </h5>
                    <div className="space-y-2">
                      {getDetailedWidgets().map((widget, i) => {
                        const previewItem = preview.widgets.find(p => p.name === widget.name);
                        const config = widget.defaultConfig || {};

                        return (
                          <div key={i} className={`p-3 rounded-lg border ${previewItem?.hasConflict ? 'border-warning-300 bg-warning-50' : 'border-neutral-200 bg-surface'}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{getWidgetTypeIcon(widget.type)}</span>
                                <div>
                                  <span className="font-medium text-neutral-900">{widget.name}</span>
                                  <span className="text-xs text-neutral-500 ml-2">({widget.type})</span>
                                </div>
                              </div>
                              {previewItem?.hasConflict && (
                                <span className="text-xs bg-warning-200 text-warning-800 px-2 py-1 rounded">
                                  Conflict
                                </span>
                              )}
                            </div>
                            {widget.description && (
                              <p className="text-sm text-neutral-600 mt-1 ml-8">{widget.description}</p>
                            )}
                            {/* Show key configuration details */}
                            <div className="mt-1 ml-8 text-xs text-neutral-500">
                              {widget.type === 'form' && config.fields ? (
                                <span>{(config.fields as unknown[]).length} form fields</span>
                              ) : widget.type === 'qa' && config.categories ? (
                                <span>{(config.categories as unknown[]).length} Q&A categories</span>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className="mt-4 p-3 bg-neutral-100 rounded-lg text-sm text-neutral-600">
                  <strong>Summary:</strong> This import will create
                  {preview.workflows.length > 0 && ` ${preview.workflows.length} workflow`}
                  {preview.dashboards.length > 0 && ` ${preview.dashboards.length} dashboard${preview.dashboards.length > 1 ? 's' : ''}`}
                  {preview.widgets.length > 0 && ` ${preview.widgets.length} widget template${preview.widgets.length > 1 ? 's' : ''}`}
                  {preview.conflictsCount > 0 && (
                    <span className="text-warning-600"> ({preview.conflictsCount} conflict{preview.conflictsCount > 1 ? 's' : ''})</span>
                  )}
                </div>
              </div>

              {/* Conflict Resolution Options */}
              {preview.conflictsCount > 0 && (
                <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                  <h4 className="font-medium text-warning-800 mb-3">
                    {preview.conflictsCount} Conflict{preview.conflictsCount > 1 ? 's' : ''} Found
                  </h4>
                  <p className="text-sm text-warning-700 mb-3">
                    You can rename conflicting items directly above, or choose how to handle remaining conflicts:
                  </p>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="conflict"
                        value="rename"
                        checked={conflictResolution === 'rename'}
                        onChange={() => setConflictResolution('rename')}
                        className="w-4 h-4 text-primary-600"
                      />
                      <div>
                        <span className="font-medium">Auto-Rename</span>
                        <span className="text-sm text-neutral-600 ml-2">Add "(Imported)" suffix to un-renamed conflicts</span>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="conflict"
                        value="skip"
                        checked={conflictResolution === 'skip'}
                        onChange={() => setConflictResolution('skip')}
                        className="w-4 h-4 text-primary-600"
                      />
                      <div>
                        <span className="font-medium">Skip</span>
                        <span className="text-sm text-neutral-600 ml-2">Don't import un-renamed conflicting items</span>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="conflict"
                        value="overwrite"
                        checked={conflictResolution === 'overwrite'}
                        onChange={() => setConflictResolution('overwrite')}
                        className="w-4 h-4 text-primary-600"
                      />
                      <div>
                        <span className="font-medium">Overwrite</span>
                        <span className="text-sm text-neutral-600 ml-2">Replace existing items with imported ones</span>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Workflow Options */}
              {preview.workflows.length > 0 && (
                <div className="border border-neutral-200 rounded-lg p-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={importAsDraft}
                      onChange={(e) => setImportAsDraft(e.target.checked)}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                    <div>
                      <span className="font-medium">Import as Draft</span>
                      <span className="text-sm text-neutral-600 ml-2">Workflow will be in draft status after import</span>
                    </div>
                  </label>
                </div>
              )}

              {error && (
                <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 text-danger-700">
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Importing Step */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-lg font-medium text-neutral-900">Importing...</p>
              <p className="text-neutral-500">Please wait while your items are being imported.</p>
            </div>
          )}

          {/* Complete Step */}
          {step === 'complete' && result && (
            <div className="space-y-6">
              {result.success ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-neutral-900 mb-2">Import Successful!</h3>
                  <p className="text-neutral-600">Your items have been imported successfully.</p>
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-danger-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-danger-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-neutral-900 mb-2">Import Failed</h3>
                  <p className="text-neutral-600">There were errors during the import.</p>
                </div>
              )}

              {/* Import Summary */}
              <div className="bg-neutral-50 rounded-lg p-4 space-y-3">
                {result.widgetsImported.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-neutral-700">Widgets</h4>
                    {result.widgetsImported.map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-1">
                        <span>{item.name}</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          item.status === 'created' ? 'bg-success-100 text-success-700' :
                          item.status === 'overwritten' ? 'bg-primary-100 text-primary-700' :
                          'bg-neutral-100 text-neutral-700'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {result.dashboardsImported.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-neutral-700">Dashboards</h4>
                    {result.dashboardsImported.map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-1">
                        <span>{item.name}</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          item.status === 'created' ? 'bg-success-100 text-success-700' :
                          item.status === 'overwritten' ? 'bg-primary-100 text-primary-700' :
                          'bg-neutral-100 text-neutral-700'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {result.workflowsImported.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-neutral-700">Workflows</h4>
                    {result.workflowsImported.map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-1">
                        <span>{item.name}</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          item.status === 'created' ? 'bg-success-100 text-success-700' :
                          item.status === 'overwritten' ? 'bg-primary-100 text-primary-700' :
                          'bg-neutral-100 text-neutral-700'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Errors */}
              {result.errors.length > 0 && (
                <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
                  <h4 className="font-medium text-danger-800 mb-2">Errors</h4>
                  <ul className="list-disc list-inside text-danger-700 text-sm space-y-1">
                    {result.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
          {step === 'upload' && (
            <Button variant="outline" size="sm" onClick={handleClose}>
              Cancel
            </Button>
          )}

          {step === 'preview' && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  resetState();
                  setStep('upload');
                }}
              >
                Back
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleImport}
                disabled={loading || (preview?.validationErrors?.length ?? 0) > 0}
                loading={loading}
              >
                Import
              </Button>
            </>
          )}

          {step === 'complete' && (
            <Button variant="primary" size="sm" onClick={handleClose}>
              Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportModal;
